/**
 * Default padding for UV insert TEXT elements, per font, in em.
 *
 * Script fonts whose ink overhangs the line box need room inside the textarea's padding box (the
 * clip edge). Amalfi Coast, measured via canvas at 100px with line-height 1.7: capitals overshoot
 * the top by ~0.25em, H/p/g overhang the left by ~0.2em, and J's tail descends ~0.67em below the
 * line box (y/g ~0.2em). Values are in em so they follow the auto-shrunk size (ALBAU-872).
 *
 * This is the single source for BOTH renderers: the DOM (`VisualiserTextBox.vm` via
 * `text-box-layout.ts`) applies it as CSS, and the canvas cart preview (`renderCardPreview`)
 * resolves it to px at the drawn font size. One map, so the picture in the cart cannot drift
 * from the face the customer edited. A font with no entry gets no padding.
 */

export type TextBoxPaddingEm = {
  top: number
  right: number
  bottom: number
  left: number
}

const NO_PADDING: TextBoxPaddingEm = Object.freeze({ top: 0, right: 0, bottom: 0, left: 0 })

export const TEXT_BOX_PADDING_EM: Readonly<Record<string, TextBoxPaddingEm>> = {
  "Amalfi Coast": Object.freeze({ top: 0.3, right: 0.3, bottom: 0.7, left: 0.3 }),
}

/**
 * The em padding for `font` — all zeros for a font with no entry.
 *
 * `Object.hasOwn` rather than a bare index: the font name comes from an authored config, and a
 * bare index reaches `Object.prototype`, so a family called "constructor" or "toString" would
 * hand a caller a Function instead of a padding box.
 */
export function getTextBoxPaddingEm(font: string | undefined): TextBoxPaddingEm {
  if (!font || !Object.hasOwn(TEXT_BOX_PADDING_EM, font)) return NO_PADDING
  return TEXT_BOX_PADDING_EM[font] ?? NO_PADDING
}

/**
 * The same padding as a CSS shorthand string for the DOM textarea — `"0.3em 0.3em 0.7em"` for
 * Amalfi Coast, `"0"` for everything else. Config `style.padding` may still override it.
 */
export function getDefaultTextBoxPadding(font: string | undefined): string {
  const { top, right, bottom, left } = getTextBoxPaddingEm(font)
  if (top === 0 && right === 0 && bottom === 0 && left === 0) return "0"
  const em = (n: number) => `${n}em`
  return left === right ? `${em(top)} ${em(right)} ${em(bottom)}` : `${em(top)} ${em(right)} ${em(bottom)} ${em(left)}`
}

/** The padding resolved to px at `fontSize`, for the canvas painter. */
export function getTextBoxPaddingPx(font: string | undefined, fontSize: number): TextBoxPaddingEm {
  const em = getTextBoxPaddingEm(font)
  return {
    top: em.top * fontSize,
    right: em.right * fontSize,
    bottom: em.bottom * fontSize,
    left: em.left * fontSize,
  }
}
